import {
  EQUIP_SLOTS,
  type EquippedSlots,
  type EquipSlot,
  getItemTemplate,
  isTwoHanded,
  type ItemInstance,
  type ItemTemplate,
} from "@vinyl/shared";
import { el } from "./dom.js";
import { bindItemTooltip, hideItemTooltip } from "./item-tooltip.js";
import { setupWindow, type WindowChromeHandle } from "./window-chrome.js";

export interface InventoryCallbacks {
  onEquip: (instanceId: string) => void;
  onUnequip: (slot: EquipSlot) => void;
  // M17.3 — drink/eat a consumable (clicking it in the bag).
  onUse: (instanceId: string) => void;
}

const SLOT_LABELS: Record<EquipSlot, string> = {
  Head: "Head",
  Neck: "Neck",
  Shoulders: "Shoulders",
  Chest: "Chest",
  Waist: "Waist",
  Legs: "Legs",
  Feet: "Feet",
  RightHand: "Right Hand",
  LeftHand: "Left Hand",
  Ring1: "Ring 1",
  Ring2: "Ring 2",
  Cloak: "Cloak",
  Wrist1: "Wrist 1",
  Wrist2: "Wrist 2",
};

export class InventoryPanel {
  readonly root: HTMLElement;
  private readonly chrome: WindowChromeHandle;

  private inventory: ItemInstance[] = [];
  private equipped: EquippedSlots = emptyView();

  constructor(private readonly cb: InventoryCallbacks) {
    this.root = el("aside", { class: "inventory-panel hidden" });
    this.chrome = setupWindow({
      windowEl: this.root,
      title: "Inventory",
      storageKey: "inventory",
    });
    this.render();
  }

  toggle(): void { this.chrome.toggle(); }
  open(): void { this.chrome.open(); }
  close(): void { this.chrome.close(); }

  setData(inventory: ItemInstance[], equipped: EquippedSlots): void {
    this.inventory = inventory;
    this.equipped = equipped;
    // The previous DOM (with its tooltip-bound buttons) is about to be
    // wiped. Hide the singleton so it doesn't linger anchored to a
    // destroyed element.
    hideItemTooltip();
    this.render();
  }

  destroy(): void {
    this.root.remove();
  }

  private render(): void {
    this.root.replaceChildren();

    this.root.append(
      this.chrome.header,
      this.renderEquipped(),
      this.renderInventoryGrid(),
    );
  }

  private renderEquipped(): HTMLElement {
    const wrapper = el("div", { class: "inv-equipped" });
    wrapper.append(el("div", { class: "inv-section-label" }, "Equipped"));
    const grid = el("div", { class: "inv-equipped-grid" });
    for (const slot of EQUIP_SLOTS) {
      const item = this.equipped[slot];
      const cell = el(
        "div",
        { class: `inv-slot inv-equipped-slot${item ? " filled" : " empty"}` },
        el("span", { class: "inv-slot-label" }, SLOT_LABELS[slot]),
      );
      if (item) {
        const template = getItemTemplate(item.templateId);
        if (template) {
          const isUnique = template.rarity === "unique";
          const isRare = item.affixes.length > 0;
          const rarityClass = isUnique
            ? " inv-unique"
            : isRare
              ? " inv-rare"
              : "";
          const iconStyle = template.iconPath
            ? `background-image:url("/assets/${template.iconPath}")`
            : "";
          const itemBtn = el(
            "button",
            {
              class: `inv-slot-item${rarityClass}${iconStyle ? " with-icon" : ""}`,
              type: "button",
              ...(iconStyle ? { style: iconStyle } : {}),
            },
            shortName(template),
          ) as HTMLButtonElement;
          bindItemTooltip(itemBtn, template, item);
          itemBtn.addEventListener("click", () => this.cb.onUnequip(slot));
          cell.append(itemBtn);
          if (isTwoHanded(template) && slot === "RightHand") {
            cell.append(el("span", { class: "inv-twohand-hint" }, "(2H)"));
          }
        }
      }
      grid.append(cell);
    }
    wrapper.append(grid);
    return wrapper;
  }

  private renderInventoryGrid(): HTMLElement {
    const wrapper = el("div", { class: "inv-bag" });
    const count = this.inventory.length;
    wrapper.append(
      el(
        "div",
        { class: "inv-section-label" },
        `Bag (${count} item${count === 1 ? "" : "s"})`,
      ),
    );
    const grid = el("div", { class: "inv-bag-grid" });
    for (const item of this.inventory) {
      const template = getItemTemplate(item.templateId);
      if (!template) {
        grid.append(el("div", { class: "inv-slot inv-bag-slot empty" }, "?"));
        continue;
      }
      const isUnique = template.rarity === "unique";
      const isRare = item.affixes.length > 0;
      const rarityClass = isUnique
        ? " inv-unique"
        : isRare
          ? " inv-rare"
          : "";
      const marker = isUnique ? " ◆" : isRare ? " ★" : "";
      const iconStyle = template.iconPath
        ? `background-image:url("/assets/${template.iconPath}")`
        : "";
      const btn = el(
        "button",
        {
          class: `inv-slot inv-bag-slot filled${rarityClass}${iconStyle ? " with-icon" : ""}`,
          type: "button",
          ...(iconStyle ? { style: iconStyle } : {}),
        },
        el(
          "span",
          { class: "inv-bag-name" },
          shortName(template) + marker,
        ),
      ) as HTMLButtonElement;
      bindItemTooltip(btn, template, item);
      if (template.stackable && item.stackCount > 1) {
        btn.append(el("span", { class: "inv-bag-stack" }, `×${item.stackCount}`));
      }
      // M17.3 — consumables (no equip slot) are drunk/eaten on click;
      // everything else is equipped.
      const isConsumable = template.category === "consumable";
      btn.addEventListener("click", () =>
        isConsumable
          ? this.cb.onUse(item.instanceId)
          : this.cb.onEquip(item.instanceId),
      );
      grid.append(btn);
    }
    if (count === 0) {
      grid.append(
        el("div", { class: "inv-bag-empty-hint" }, "Bag is empty."),
      );
    }
    wrapper.append(grid);
    return wrapper;
  }
}

function shortName(template: ItemTemplate): string {
  return template.name;
}

function emptyView(): EquippedSlots {
  return {
    Head: null,
    Neck: null,
    Shoulders: null,
    Chest: null,
    Waist: null,
    Legs: null,
    Feet: null,
    RightHand: null,
    LeftHand: null,
    Ring1: null,
    Ring2: null,
    Cloak: null,
    Wrist1: null,
    Wrist2: null,
  };
}
